import { formatDate } from '@/lib/format';

import type { InactivityNotice, NoticeKind } from './contracts';

/**
 * The four colours of the "Notificação · inatividade" component (Figma, Telas › Propostas): one
 * per stage, so the stage is recognised before it is read. The optional one-day warning is the
 * final warning with one day left and wears its colour.
 */
export type NoticeTone = 'warning' | 'danger' | 'inverse' | 'success';

export const NOTICE_TONE: Readonly<Record<NoticeKind, NoticeTone>> = {
  first_warning: 'warning',
  final_warning: 'danger',
  last_day: 'danger',
  blocked: 'inverse',
  reactivated: 'success',
};

/** What the notice's button does: buy, or ask for the unit back. */
export type NoticeAction = 'buy' | 'request_reactivation';

export interface NoticeCopy {
  readonly tone: NoticeTone;
  /** Small caps line above the title: "FALTAM 30 DIAS". Rendered upper-case by CSS. */
  readonly label: string;
  readonly title: string;
  readonly description: string;
  readonly actionLabel: string;
  readonly action: NoticeAction;
  /** The glyph in the round icon: `!`, `!!`, `×`, `✓`. */
  readonly glyph: string;
}

/**
 * `2026-10-20` → `20/10`. Dates on a notice are calendar days, not instants: parsing one with
 * `new Date()` would read it as UTC midnight, which is the evening before in São Paulo.
 */
export function shortDay(isoDay: string | null | undefined): string {
  if (isoDay == null) return '';
  const [, month, day] = isoDay.split('-');
  return day !== undefined && month !== undefined ? `${day}/${month}` : isoDay;
}

/** `2026-10-20` → `20/10/2026`, for the same reason as `shortDay`. */
export function fullDay(isoDay: string | null | undefined): string {
  if (isoDay == null) return '';
  const [year, month, day] = isoDay.split('-');
  return day !== undefined && month !== undefined && year !== undefined
    ? `${day}/${month}/${year}`
    : isoDay;
}

function days(count: number): string {
  return `${String(count)} ${count === 1 ? 'dia' : 'dias'}`;
}

/**
 * The copy of one notice, word for word the proposal's (I8, I9 and the component's variants), with
 * the unit, the dates and the person as variables.
 */
export function noticeCopy(notice: InactivityNotice): NoticeCopy {
  const unit = notice.establishment_name;
  const left = Math.max(notice.days_left ?? 0, 0);
  const tone = NOTICE_TONE[notice.kind];

  switch (notice.kind) {
    case 'first_warning':
      return {
        tone,
        glyph: '!',
        label: `Faltam ${days(left)}`,
        title: `A ${unit} não faz pedidos há ${days(idleDays(notice))}`,
        description: `Sem pedido até ${shortDay(notice.blocks_on)}, ela perde o acesso.`,
        actionLabel: 'Fazer um pedido',
        action: 'buy',
      };
    case 'final_warning':
    case 'last_day':
      return {
        tone,
        glyph: '!!',
        label: `Último aviso · ${days(left)}`,
        title: `${left === 1 ? 'Falta' : 'Faltam'} ${days(left)} para a ${unit} ser bloqueada`,
        description: `Qualquer pedido até ${shortDay(notice.blocks_on)} mantém o acesso.`,
        actionLabel: 'Fazer um pedido agora',
        action: 'buy',
      };
    case 'blocked':
      return {
        tone,
        glyph: '×',
        label: 'Unidade bloqueada',
        title: `A ${unit} foi bloqueada por inatividade`,
        description: `${days(notice.threshold_days ?? 0)} sem pedido. Peça a reativação.`,
        actionLabel: 'Pedir reativação',
        action: 'request_reactivation',
      };
    case 'reactivated':
      return {
        tone,
        glyph: '✓',
        label: 'Unidade reativada',
        title: `A ${unit} voltou a comprar`,
        // An instant, unlike the dates above, so it goes through the São Paulo formatter.
        description: `Reativada por ${notice.actor_label ?? 'Equipe Instoque'} em ${formatDate(
          notice.created_at,
        ).slice(0, 5)}.`,
        actionLabel: 'Ir para Comprar',
        action: 'buy',
      };
  }
}

/** Days without an order when the warning was written: the threshold minus what was left then. */
function idleDays(notice: InactivityNotice): number {
  const threshold = notice.threshold_days ?? 0;
  return Math.max(threshold - (notice.days_left ?? 0), 0);
}
